/**
 * Auto-schedule UI trigger wiring — the v2 port of
 * `gantt/sources/ext/auto_scheduling/ui_handlers.ts`.
 *
 * Attaches event handlers that route gantt UI events (link add/update/delete,
 * parse, drag, lightbox save, inline editor save, batchUpdate) to the v2
 * engine via `autoScheduleViaEngine` (from `event_bridge.ts`).
 *
 * Differences from v1:
 *  - All scheduling goes through `autoScheduleViaEngine` instead of
 *    `gantt.autoSchedule` / `gantt._autoSchedule`.
 *  - Anchor windows open at drag-start / lightbox-save and close after the
 *    paired autoSchedule call, so the engine sees pre-mutation summary
 *    bboxes through `Snapshot.anchorOverrides`.
 *  - Virtual-lag pre-computation (v1's `resetToStartLinksLags` + `relations`
 *    tracking) is dropped — D-10 per-component edge calendars re-derive
 *    SS/SF lag at snapshot-build time.
 *  - Link-add cycle guard routes through `core/api.findCycleIfAdded`, which
 *    returns the actual cycle directly (v1's `loopsFinder.getLoopContainingLink`
 *    becomes a second look-up; v2 has the cycle in hand from the same call).
 *  - The decoupling lint rule (D-33) is honored: this file imports only from
 *    `core/api`, `event_bridge`, `anchor_cache`, and `snapshot_builder`
 *    types — never from `gantt` directly.
 *
 * Day 13 will hook `attachAutoScheduleTriggers(gantt)` into
 * `gantt/sources/ext/auto_scheduling.js` under the `_engine === "v2"` flag.
 */

import type {
  ConstraintType,
  TaskID,
  TimeUnit
} from "../core/types";
import { findCycleIfAdded } from "../core/api";
import { buildCandidateLinkSnapshot } from "./link_snapshot";
import type { LinkRecord } from "./link_snapshot";
import { autoScheduleViaEngine } from "./event_bridge";
import {
  beginWindow as anchorBegin,
  endWindow as anchorEnd,
  cancelWindow as anchorCancel
} from "./anchor_cache";
import { getOrBuildSnapshot, bumpRevision } from "./gantt_adapter";
import type { GanttEventBridgeBackend } from "./event_bridge";

// ─────────────────────────────────────────────────────────────────────────────
// Structural gantt subset
// ─────────────────────────────────────────────────────────────────────────────

export interface TaskRecord {
  id: TaskID;
  start_date?: Date;
  end_date?: Date;
  duration?: number;
  type?: string;
  progress?: number;
  constraint_type?: ConstraintType;
  constraint_date?: Date;
  auto_scheduling?: boolean;
  $keep_constraints?: boolean;
  [key: string]: unknown;
}

interface InlineEditorState {
  id: TaskID;
  columnName: string;
}

interface InlineEditorAPI {
  attachEvent: (name: string, handler: (state: InlineEditorState) => unknown) => unknown;
  getEditorConfig?: (columnName: string) => { map_to?: string } | undefined;
}

export interface GanttTriggersBackend extends GanttEventBridgeBackend {
  attachEvent: (name: string, handler: (...args: unknown[]) => unknown, opts?: { once?: boolean }) => unknown;
  _getPredecessors: (task: TaskRecord) => Array<{ source: TaskID; target: TaskID }>;
  _hasDuration: (cfg: { start_date: Date; end_date: Date; task?: TaskRecord }) => boolean;
  isChildOf: (descendantId: TaskID, ancestorId: TaskID) => boolean;
  getConstraintType: (task: TaskRecord) => ConstraintType | undefined;
  calculateDuration: (cfg: { start_date?: Date; end_date?: Date; task?: TaskRecord; unit?: TimeUnit } | TaskRecord) => number;
  config: GanttEventBridgeBackend["config"] & {
    constraint_types?: Record<string, ConstraintType>;
    links?: { start_to_start?: unknown; start_to_finish?: unknown; finish_to_start?: unknown; finish_to_finish?: unknown };
    inline_editors_date_processing?: string;
  };
  ext?: { inlineEditors?: InlineEditorAPI };
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-instance trigger state (kept on a WeakMap so multi-gantt-instance pages
// don't conflict; matches the pattern used by anchor_cache and the snapshot
// cache).
// ─────────────────────────────────────────────────────────────────────────────

interface TriggerState {
  movedTaskId: TaskID | null;
  draggedTask: Map<TaskID, TaskRecord>;
  dragTimeout: ReturnType<typeof setTimeout> | null;
  modifiedTaskId: TaskID | null;
  changedConstraint: boolean;
  scheduleAfterBatchUpdate: boolean;
  attached: boolean;
}

const stateByGantt = new WeakMap<object, TriggerState>();

function getState(gantt: GanttTriggersBackend): TriggerState {
  let s = stateByGantt.get(gantt as unknown as object);
  if (!s) {
    s = {
      movedTaskId: null,
      draggedTask: new Map(),
      dragTimeout: null,
      modifiedTaskId: null,
      changedConstraint: false,
      scheduleAfterBatchUpdate: false,
      attached: false
    };
    stateByGantt.set(gantt as unknown as object, s);
  }
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers ported from v1
// ─────────────────────────────────────────────────────────────────────────────

function getAutoSchedulingConfig(gantt: GanttTriggersBackend): Record<string, unknown> {
  return gantt._getAutoSchedulingConfig
    ? (gantt._getAutoSchedulingConfig() as Record<string, unknown>)
    : {};
}

function isEnabled(gantt: GanttTriggersBackend): boolean {
  return getAutoSchedulingConfig(gantt).enabled === true;
}

function inProgress(gantt: GanttTriggersBackend): boolean {
  return gantt._autoscheduling_in_progress === true;
}

function inBatchUpdate(gantt: GanttTriggersBackend): boolean {
  const state = gantt.getState();
  return state && (state as { batch_update?: boolean }).batch_update === true;
}

function _datesNotEqual(
  dateA: Date | undefined,
  dateB: Date | undefined,
  taskA: TaskRecord,
  taskB: TaskRecord,
  gantt: GanttTriggersBackend
): boolean {
  if (!!dateA !== !!dateB) return true;
  if (!dateA || !dateB) return false;
  if (dateA.valueOf() > dateB.valueOf()) {
    return gantt._hasDuration({ start_date: dateB, end_date: dateA, task: taskB });
  } else {
    return gantt._hasDuration({ start_date: dateA, end_date: dateB, task: taskA });
  }
}

/**
 * v1 `_notEqualTaskDates` — detects "user-facing change" warranting a
 * reschedule. Mirrors `ui_handlers.ts:144-172`. Compares start, constraint
 * type, constraint date, end/duration (skipping end/duration check for
 * milestones).
 */
function _notEqualTaskDates(
  oldTask: TaskRecord,
  newTask: TaskRecord,
  gantt: GanttTriggersBackend
): boolean {
  if (_datesNotEqual(oldTask.start_date, newTask.start_date, oldTask, newTask, gantt)) return true;
  if (gantt.getConstraintType(oldTask) !== gantt.getConstraintType(newTask)) return true;
  if (_datesNotEqual(oldTask.constraint_date, newTask.constraint_date, oldTask, newTask, gantt)) return true;
  const milestoneType = gantt.config?.types?.milestone;
  if (
    (_datesNotEqual(oldTask.end_date, newTask.end_date, oldTask, newTask, gantt) ||
      oldTask.duration !== newTask.duration) &&
    oldTask.type !== milestoneType
  ) {
    return true;
  }
  return false;
}

/**
 * v1 `shouldKeepConstraints` — when the user changed only the duration/end
 * (forward mode) or duration/start (backward mode), the existing constraint
 * type/date should NOT be re-derived. Returns true iff the lead anchor
 * matches between old and new.
 */
function shouldKeepConstraints(
  newTask: TaskRecord,
  oldTask: TaskRecord,
  gantt: GanttTriggersBackend
): boolean {
  const config = getAutoSchedulingConfig(gantt);
  if (config.schedule_from_end) {
    if (oldTask.end_date && newTask.end_date &&
        newTask.end_date.valueOf() === oldTask.end_date.valueOf()) {
      return true;
    }
  } else {
    if (oldTask.start_date && newTask.start_date &&
        newTask.start_date.valueOf() === oldTask.start_date.valueOf()) {
      return true;
    }
  }
  return false;
}

/**
 * Default constraint types per direction (kept as string literals — match
 * the v2 `ConstraintType` union and v1 `constraint_types.ts` enum values).
 */
const FORWARD_HARDS: ReadonlySet<ConstraintType> = new Set<ConstraintType>(["snet", "fnet", "mso", "mfo"]);
const BACKWARD_HARDS: ReadonlySet<ConstraintType> = new Set<ConstraintType>(["snlt", "fnlt", "mso", "mfo"]);

/**
 * v1 `updateTaskConstraints` (`ui_handlers.ts:257-309`). Sets the task's
 * `constraint_type` + `constraint_date` from the dragged dates, respecting
 * `schedule_from_end`. If the existing constraint type already belongs to
 * the direction's hard set, it's preserved; otherwise it's reset to the
 * direction default (SNET forward, FNLT backward).
 */
function updateTaskConstraints(task: TaskRecord, gantt: GanttTriggersBackend): void {
  if (task.auto_scheduling === false) return;
  const config = getAutoSchedulingConfig(gantt);
  if (config.schedule_from_end) {
    if (task.constraint_type && BACKWARD_HARDS.has(task.constraint_type)) {
      // Keep type, update date to track the new task position.
      if (task.constraint_type === "snlt" || task.constraint_type === "mso") {
        task.constraint_date = task.start_date ? new Date(task.start_date) : undefined;
      } else {
        task.constraint_date = task.end_date ? new Date(task.end_date) : undefined;
      }
    } else {
      task.constraint_type = "fnlt";
      task.constraint_date = task.end_date ? new Date(task.end_date) : undefined;
    }
  } else {
    if (task.constraint_type && FORWARD_HARDS.has(task.constraint_type)) {
      if (task.constraint_type === "snet" || task.constraint_type === "mso") {
        task.constraint_date = task.start_date ? new Date(task.start_date) : undefined;
      } else {
        task.constraint_date = task.end_date ? new Date(task.end_date) : undefined;
      }
    } else {
      task.constraint_type = "snet";
      task.constraint_date = task.start_date ? new Date(task.start_date) : undefined;
    }
  }
}

/**
 * v1 `finalizeTaskConstraints` (`ui_handlers.ts:311-317`). Clears the
 * constraint when `apply_constraints:false` (preferred-date mode). Keeps
 * the constraint clutter out of the data when the user isn't using
 * constraint mode.
 */
function finalizeTaskConstraints(task: TaskRecord, gantt: GanttTriggersBackend): void {
  const config = getAutoSchedulingConfig(gantt);
  if (!config.apply_constraints) {
    task.constraint_type = undefined;
    task.constraint_date = undefined;
  }
}

/**
 * v1 `_processConstraints(taskId, oldTask)` (`ui_handlers.ts:319-326`).
 */
function processConstraints(
  taskId: TaskID,
  oldTask: TaskRecord,
  gantt: GanttTriggersBackend
): void {
  const newTask = gantt.getTask(taskId) as TaskRecord;
  if (shouldKeepConstraints(newTask, oldTask, gantt)) return;
  updateTaskConstraints(newTask, gantt);
}

/**
 * v1's `_isKeepConstraintsColumn` logic (`ui_handlers.ts:399-408`) collapsed
 * into a single predicate. True when editing a column that should NOT cause
 * the engine to re-derive constraints from the new dates.
 */
function isKeepConstraintsInlineColumn(
  columnName: string,
  gantt: GanttTriggersBackend
): boolean {
  const cfg = getAutoSchedulingConfig(gantt);
  if (columnName === "duration") return true;
  if (columnName === "constraint_date") return true;
  const linkedDateProcessing = gantt.config.inline_editors_date_processing !== "keepDuration";
  const startDateCol = cfg.schedule_from_end && columnName === "start_date";
  const endDateCol = !cfg.schedule_from_end && columnName === "end_date";
  return linkedDateProcessing && (!!startDateCol || !!endDateCol);
}

// ─────────────────────────────────────────────────────────────────────────────
// Trigger handler factory
// ─────────────────────────────────────────────────────────────────────────────

function makeHandlers(gantt: GanttTriggersBackend) {
  const state = getState(gantt);

  function _autoScheduleAfterLinkChange(_id: TaskID, link: LinkRecord): void {
    if (!isEnabled(gantt) || inProgress(gantt)) return;
    if (inBatchUpdate(gantt)) {
      state.scheduleAfterBatchUpdate = true;
    } else {
      autoScheduleViaEngine(gantt, link.source, true);
    }
  }

  function onAfterLinkDelete(_id: TaskID, link: LinkRecord): void {
    if (!isEnabled(gantt) || inProgress(gantt)) return;
    if (!gantt.isTaskExists(link.target)) return;
    const target = gantt.getTask(link.target) as TaskRecord;
    const preds = gantt._getPredecessors(target);
    if (!preds.length) return;
    if (inBatchUpdate(gantt)) {
      state.scheduleAfterBatchUpdate = true;
    } else {
      autoScheduleViaEngine(gantt, preds[0].source, false);
    }
  }

  function onParse(): void {
    const cfg = getAutoSchedulingConfig(gantt);
    if (cfg.enabled && cfg.schedule_on_parse) {
      autoScheduleViaEngine(gantt);
    }
  }

  function onAfterBatchUpdate(): void {
    if (!state.scheduleAfterBatchUpdate) return;
    state.scheduleAfterBatchUpdate = false;
    autoScheduleViaEngine(gantt);
  }

  function onBeforeTaskDrag(id: TaskID, mode: string, _task: TaskRecord): boolean {
    if (!isEnabled(gantt)) return true;
    if (mode === "progress") return true;
    anchorCancel(gantt);
    anchorBegin(gantt);
    state.movedTaskId = id;
    return true;
  }

  function onBeforeTaskChanged(id: TaskID, _mode: string, task: TaskRecord): boolean {
    processConstraints(id, task, gantt);
    state.draggedTask.set(id, task);
    return true;
  }

  function _autoScheduleAfterDND(taskId: TaskID, oldTask: TaskRecord | undefined): void {
    if (!isEnabled(gantt) || inProgress(gantt)) return;
    if (!oldTask) return;
    const newTask = gantt.getTask(taskId) as TaskRecord;
    if (!_notEqualTaskDates(oldTask, newTask, gantt)) return;

    processConstraints(taskId, oldTask, gantt);

    const cfg = getAutoSchedulingConfig(gantt);
    const progressScheduling =
      cfg.use_progress === true &&
      ((oldTask.progress === 1) !== (newTask.progress === 1));
    if (progressScheduling) {
      autoScheduleViaEngine(gantt);
    } else {
      autoScheduleViaEngine(gantt, taskId, true);
    }
    finalizeTaskConstraints(newTask, gantt);
    // `processConstraints` set `constraint_type/constraint_date` on the live
    // task and `finalizeTaskConstraints` cleared them under
    // `apply_constraints:false`. Both mutate the task object directly without
    // routing through `gantt.updateTask`, so no event fires and the v2
    // snapshot cache stays at the post-`autoScheduleViaEngine` revision —
    // which captured the task WITH the SNET constraint that
    // `processConstraints` had just installed. A subsequent
    // `gantt.autoSchedule()` (e.g. the explicit call right after a drag in
    // test `11_auto_scheduling/002`) would reuse the stale snapshot, see
    // SNET=<drag target date>, and clamp the task back to that date instead
    // of compressing it. v1 doesn't have this problem because its planner
    // reads `task.constraint_type` directly from `gantt.getTask(id)` each
    // call. Mirror that for v2 by bumping the revision tracker — the next
    // snapshot rebuild then sees the cleared constraint.
    bumpRevision(gantt);
  }

  function onAfterTaskDrag(id: TaskID, _mode: string, _e: unknown): void {
    // Loose-equal: same string/number id mismatch as `onAfterTaskUpdate`.
    if (id != state.movedTaskId) return;
    if (state.dragTimeout) clearTimeout(state.dragTimeout);
    const draggedSnapshot = state.draggedTask.get(id);
    state.dragTimeout = setTimeout(() => {
      try {
        _autoScheduleAfterDND(id, draggedSnapshot);
      } finally {
        anchorEnd(gantt);
        state.movedTaskId = null;
        state.draggedTask.delete(id);
        state.dragTimeout = null;
      }
    }, 0);
  }

  function onLightboxSave(id: TaskID, task: TaskRecord): boolean {
    if (!isEnabled(gantt) || inProgress(gantt)) return true;
    const oldTask = gantt.getTask(id) as TaskRecord;
    if (!_notEqualTaskDates(oldTask, task, gantt)) return true;
    anchorCancel(gantt);
    anchorBegin(gantt);
    state.modifiedTaskId = id;
    state.changedConstraint =
      gantt.getConstraintType(task) !== gantt.getConstraintType(oldTask) ||
      Number(task.constraint_date) !== Number(oldTask.constraint_date);
    if (shouldKeepConstraints(task, oldTask, gantt)) {
      task.$keep_constraints = true;
    }
    return true;
  }

  function onAfterTaskUpdate(id: TaskID, task: TaskRecord): boolean {
    if (!isEnabled(gantt) || inProgress(gantt)) return true;
    // Loose-equal comparison: gantt's events fire with whatever type the
    // user supplied (string or number id), and `state.modifiedTaskId` is
    // assigned from one source (drag/lightbox/inline) and read against
    // another (`onAfterTaskUpdate`). v1's handler uses `==` here; matching
    // that prevents the "edit fires but constraint update is skipped" path
    // that retest 11_auto_scheduling/059..064 (keep_constraints_inline)
    // hit when the page-level task id is string `"3"` but `onAfterTaskUpdate`
    // fires with number `3` (or vice versa).
    if (state.modifiedTaskId != id) return true;
    try {
      if (task.$keep_constraints) {
        delete task.$keep_constraints;
      } else if (!state.changedConstraint) {
        updateTaskConstraints(task, gantt);
      }
      autoScheduleViaEngine(gantt, id, true);
      if (!state.changedConstraint) {
        finalizeTaskConstraints(task, gantt);
        // Bump after the constraint mutations so a follow-up explicit
        // autoSchedule rebuilds the snapshot — see `_autoScheduleAfterDND`
        // comment for the same v1↔v2 parity issue.
        bumpRevision(gantt);
      }
    } finally {
      anchorEnd(gantt);
      state.modifiedTaskId = null;
      state.changedConstraint = false;
    }
    return true;
  }

  function onInlineEditorBeforeSave(s: InlineEditorState): boolean {
    if (!isEnabled(gantt) || inProgress(gantt)) return true;
    const editor = gantt.ext?.inlineEditors;
    if (!editor) return true;
    const editorConfig = editor.getEditorConfig?.(s.columnName);
    if (!editorConfig) return true;
    if (
      editorConfig.map_to !== "start_date" &&
      editorConfig.map_to !== "end_date" &&
      editorConfig.map_to !== "duration"
    ) {
      return true;
    }
    anchorCancel(gantt);
    anchorBegin(gantt);
    state.modifiedTaskId = s.id;
    state.changedConstraint = s.columnName === "constraint_type";
    if (isKeepConstraintsInlineColumn(s.columnName, gantt)) {
      const task = gantt.getTask(s.id) as TaskRecord;
      task.$keep_constraints = true;
    }
    return true;
  }

  function onBeforeLinkAdd(_id: TaskID, link: LinkRecord): boolean {
    return _validateLinkCandidate(link);
  }

  function onBeforeLinkUpdate(_id: TaskID, link: LinkRecord): boolean {
    return _validateLinkCandidate(link);
  }

  function _validateLinkCandidate(link: LinkRecord): boolean {
    // Cycle check via the engine — pure, no `use_progress` toggling required
    // (the analysis graph used here includes all tasks per D-12 cycle-detection).
    const durationUnit: TimeUnit = (gantt.config?.duration_unit as TimeUnit) ?? "day";
    const snap = getOrBuildSnapshot(gantt);
    const candidate = buildCandidateLinkSnapshot(link, durationUnit);
    const cycle = findCycleIfAdded(snap, candidate);
    if (cycle) {
      gantt.callEvent("onCircularLinkError", [link, cycle]);
      return false;
    }
    // Descendant-link guard. `descendant_links:false` rejects parent-child
    // links to/from a summary (either direction).
    const cfg = getAutoSchedulingConfig(gantt);
    if (cfg.descendant_links === false) {
      const source = gantt.getTask(link.source) as TaskRecord;
      const target = gantt.getTask(link.target) as TaskRecord;
      const sourceIsSummary = gantt.isSummaryTask?.(source) === true;
      const targetIsSummary = gantt.isSummaryTask?.(target) === true;
      if (
        (gantt.isChildOf(source.id, target.id) && targetIsSummary) ||
        (gantt.isChildOf(target.id, source.id) && sourceIsSummary)
      ) {
        return false;
      }
    }
    return true;
  }

  return {
    onAfterLinkChange: _autoScheduleAfterLinkChange,
    onAfterLinkDelete,
    onParse,
    onBeforeTaskDrag,
    onBeforeTaskChanged,
    onAfterTaskDrag,
    onLightboxSave,
    onAfterTaskUpdate,
    onAfterBatchUpdate,
    onBeforeLinkAdd,
    onBeforeLinkUpdate,
    onInlineEditorBeforeSave
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Public surface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Attach v2 auto-schedule triggers to a gantt instance. Idempotent —
 * subsequent calls on the same gantt instance are no-ops.
 *
 * Per the v1 pattern (`ui_handlers.ts:503-506`), handler attachment is
 * deferred to `onGanttReady` (fire-once) so the wiring happens after gantt
 * is fully initialized.
 */
export function attachAutoScheduleTriggers(gantt: GanttTriggersBackend): void {
  const state = getState(gantt);
  if (state.attached) return;
  state.attached = true;

  gantt.attachEvent("onGanttReady", () => {
    const h = makeHandlers(gantt);

    gantt.attachEvent("onAfterLinkUpdate",   h.onAfterLinkChange);
    gantt.attachEvent("onAfterLinkAdd",      h.onAfterLinkChange);
    gantt.attachEvent("onAfterLinkDelete",   h.onAfterLinkDelete);
    gantt.attachEvent("onParse",             h.onParse);
    gantt.attachEvent("onBeforeTaskDrag",    h.onBeforeTaskDrag);
    gantt.attachEvent("onBeforeTaskChanged", h.onBeforeTaskChanged);
    gantt.attachEvent("onAfterTaskDrag",     h.onAfterTaskDrag);
    gantt.attachEvent("onLightboxSave",      h.onLightboxSave);
    gantt.attachEvent("onAfterTaskUpdate",   h.onAfterTaskUpdate);
    gantt.attachEvent("onAfterBatchUpdate",  h.onAfterBatchUpdate);
    gantt.attachEvent("onBeforeLinkAdd",     h.onBeforeLinkAdd);
    gantt.attachEvent("onBeforeLinkUpdate",  h.onBeforeLinkUpdate);

    const inlineEditors = gantt.ext?.inlineEditors;
    if (inlineEditors) {
      inlineEditors.attachEvent("onBeforeSave", h.onInlineEditorBeforeSave);
    }
  }, { once: true });
}

/**
 * Test-only access to the named handlers without running the
 * `onGanttReady`-gated wiring. Production code should call
 * `attachAutoScheduleTriggers(gantt)`.
 */
export function _makeHandlersForTest(gantt: GanttTriggersBackend) {
  return makeHandlers(gantt);
}

// Helper re-exports for the trigger module's consumers (Day 13's
// `ext/auto_scheduling.js` shim and tests).
export { processConstraints, updateTaskConstraints, finalizeTaskConstraints, shouldKeepConstraints };
